import React from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faCheck,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import {
  ANIMATIONS,
  popAndBounce,
} from 'shared/design-system/settings/variables/Animation';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { CheckboxWhite } from '../../components/Checkbox';
import Radio from '../../components/Radio';
import { textSelectionNone } from '../base/TrueImpactText';

interface SelectorProps {
  margin: TrueImpactSelectorProps['margin'];
  padding: TrueImpactSelectorProps['padding'];
  maxWidth: TrueImpactSelectorProps['maxWidth'];
  color: TrueImpactSelectorProps['color'];
  disabled: TrueImpactSelectorProps['disabled'];
  backgroundColor: TrueImpactSelectorProps['backgroundColor'];
  backgroundColorHover: TrueImpactSelectorProps['backgroundColorHover'];
  selected: TrueImpactSelectorProps['selected'];
  animationScale: TrueImpactSelectorProps['animationScale'];
}

const SelectorBase = styled.div<SelectorProps>`
  font-family: ${TYPOGRAPHY.fontFamilyBody};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  font-size: ${TYPOGRAPHY.bodyBase};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  outline: 1px solid
    ${({ disabled }) => (disabled ? COLORS.greyBase : COLORS.ctaPrimary)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: 4px;
  text-align: center;
  ${textSelectionNone}
  ${({ margin }) => (margin ? `margin: ${margin};` : undefined)}
  ${({ padding }) =>
    padding ? `padding: ${padding} !important;` : `${SPACERS.small};`}
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth};` : undefined)}
  background-color: ${({ selected }) =>
    selected ? COLORS.ctaPrimary : COLORS.whiteBase};
  color: ${({ selected, disabled }) =>
    disabled
      ? COLORS.grayBase
      : selected
      ? COLORS.whiteBase
      : COLORS.blackBase};
  &:hover {
    background-color: ${({ selected }) =>
      selected ? COLORS.ctaPrimary : COLORS.ctaPrimaryBackground};
    transition: background-color 150ms ${ANIMATIONS.easing};
  }
  .icon {
    margin-top: 24px;
    margin-bottom: 12px;
    color: ${({ selected }) =>
      selected ? COLORS.whiteBase : COLORS.ctaPrimary};
  }
  .text,
  .icon-text {
    color: ${({ selected }) => (selected ? COLORS.whiteBase : 'unset')};
  }
  .text {
    margin-bottom: 10px;
  }
  svg {
    &.bounceEnter {
      animation: ${popAndBounce} 1s ${ANIMATIONS.easing};
      animation-timing-function: ${ANIMATIONS.easing};
      animation-duration: 1s;
      animation-fill-mode: both;
    }
  }
`;

const SelectorPrimary = styled(SelectorBase)`
  flex-direction: column;
  padding: ${SPACERS.large};
  gap: ${SPACERS.small};
  align-items: center;
  .main-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    svg {
      color: ${({ selected }) =>
        selected ? COLORS.whiteBase : COLORS.ctaPrimary};
      font-size: 32px;
      &.bounceEnter {
        animation: ${popAndBounce} 1s ${ANIMATIONS.easing};
        animation-timing-function: ${ANIMATIONS.easing};
        animation-duration: 1s;
        animation-fill-mode: both;
      }
    }
  }
`;

const SelectorPrimaryDetailedRadio = styled(SelectorBase)`
  gap: ${SPACERS.small};
  align-items: center;
  &:hover {
    background-color: ${({ selected }) =>
      selected ? COLORS.ctaPrimary : 'initial'};
  }
  .main-group {
    width: 100%;
    display: flex;
    .left-side {
      padding: ${SPACERS.large};
      width: 196px;
      display: flex;
      align-items: center;
      gap: ${SPACERS.tiny};
      flex-direction: column;
    }

    &:hover {
      .left-side,
      .right-side {
        transition: background-color 150ms ${ANIMATIONS.easing};
      }
      .left-side {
        background-color: ${({ selected }) =>
          selected ? COLORS.ctaPrimary : COLORS.greyLightest};
      }
      .right-side {
        background-color: ${({ selected }) =>
          selected ? COLORS.ctaPrimaryBackground : COLORS.ctaPrimaryBackground};
      }
    }

    .right-side {
      flex: 1;
      text-align: left;
      padding: ${SPACERS.small};
      background-color: ${({ selected }) =>
        selected ? COLORS.ctaPrimaryBackground : COLORS.grayLightest};
    }
  }
  .icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    svg {
      color: ${({ selected }) =>
        selected ? COLORS.whiteBase : COLORS.ctaPrimary};
      font-size: 32px;
      &.bounceEnter {
        animation: ${popAndBounce} 1s ${ANIMATIONS.easing};
        animation-timing-function: ${ANIMATIONS.easing};
        animation-duration: 1s;
        animation-fill-mode: both;
      }
    }
  }
`;

const SelectorSecondary = styled(SelectorBase)<{
  selected?: boolean;
}>`
  flex-direction: row;
  padding: ${SPACERS.small};
  gap: ${SPACERS.small};
  text-align: left;
  .main-group {
    display: flex;
    flex-direction: row;
    gap: ${SPACERS.tiny};
    align-items: center;
    justify-content: center;
  }
  .icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    svg {
      font-size: 24px;
      color: ${({ selected }) =>
        selected ? COLORS.whiteBase : COLORS.ctaPrimary};
    }
  }
`;

const SelectorSecondaryNoIcon = styled(SelectorSecondary)``;

interface TrueImpactSelectorProps {
  margin?: string;
  padding?: string;
  maxWidth?: string;
  animationScale?: number;
  disabled?: boolean;
  titleText?: string;
  variant?:
    | 'primary-detailed-radio'
    | 'primary-checkbox'
    | 'primary-radio'
    | 'secondary'
    | 'secondary-no-icon'
    | 'checkbox'
    | 'radio';
  className?: string;
  id?: string;
  icon?: IconProp;
  label?: string | React.ReactNode | React.ReactNode[];
  tooltipComponent?: React.ReactNode;
  color?: keyof typeof COLORS;
  backgroundColor?: keyof typeof COLORS;
  backgroundColorHover?: keyof typeof COLORS;
  selected?: boolean;
  onClick?: () => unknown;
}

export const Selector: React.FC<TrueImpactSelectorProps> = ({
  variant = 'radio',
  className,
  id,
  icon,
  label,
  margin,
  padding,
  maxWidth,
  animationScale,
  tooltipComponent,
  color,
  backgroundColor,
  backgroundColorHover,
  selected,
  disabled,
  titleText,
  onClick,
}) => {
  switch (variant) {
    case 'primary-detailed-radio':
      return (
        <SelectorPrimaryDetailedRadio
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          className={`ti-sel-opt ${className} ${selected ? 'sel' : ''}`}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            <div className="left-side">
              {icon && (
                <div className="icon-container">
                  {!selected && <FontAwesomeIcon icon={icon} />}
                  {selected && <Radio variant="white" checked={selected} />}
                </div>
              )}
              <div className="label">{label}</div>
            </div>
            <div className="right-side">{tooltipComponent}</div>
          </div>
        </SelectorPrimaryDetailedRadio>
      );

    case 'checkbox':
      return (
        <SelectorSecondary
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          className={`ti-sel-opt ${className} ${selected ? 'sel' : ''}`}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            <div className="icon-container">
              <CheckboxWhite
                checked={selected || false}
                className={selected ? 'bounceEnter' : ''}
              />
            </div>
            <div className="label">{label}</div>
          </div>
          {tooltipComponent && (
            <Tooltip arrow={true} title={tooltipComponent}>
              {/* div is required for MUI Tooltip reference */}
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
              </div>
            </Tooltip>
          )}
        </SelectorSecondary>
      );
    case 'primary-checkbox':
      return (
        <SelectorPrimary
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          className={`ti-sel-opt ${className} ${selected ? 'sel' : ''}`}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            {icon && (
              <div className="icon-container">
                <FontAwesomeIcon
                  icon={!selected || label === 'No' ? icon : faCheck}
                  className={selected ? 'bounceEnter' : 'bounceExit'}
                  color={selected ? COLORS.whiteBase : COLORS.ctaPrimary}
                />
              </div>
            )}
            <div className="label">{label}</div>
          </div>
          {tooltipComponent && (
            <Tooltip arrow={true} title={tooltipComponent}>
              {/* div is required for MUI Tooltip reference */}
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
              </div>
            </Tooltip>
          )}
        </SelectorPrimary>
      );
    case 'primary-radio':
      return (
        <div>
          <SelectorPrimary
            margin={margin}
            padding={padding}
            maxWidth={maxWidth}
            animationScale={animationScale}
            className={`ti-sel-opt ${className || ''} ${
              selected ? 'sel' : ''
            }`.trim()}
            id={id}
            onClick={disabled ? undefined : onClick}
            selected={selected}
            color={color}
            disabled={disabled}
            backgroundColor={backgroundColor}
            backgroundColorHover={backgroundColorHover}
            title={titleText}
          >
            <div className="main-group">
              {icon && (
                <div className="icon-container">
                  {!selected && <FontAwesomeIcon icon={icon} />}
                  {selected && <Radio variant="white" checked={selected} />}
                </div>
              )}
              <div className="label">{label}</div>
            </div>
            {tooltipComponent && (
              <Tooltip arrow={true} title={tooltipComponent}>
                {/* div is required for MUI Tooltip reference */}
                <div>
                  <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
                </div>
              </Tooltip>
            )}
          </SelectorPrimary>
        </div>
      );
    case 'secondary':
      return (
        <SelectorSecondary
          className={`ti-sel-opt ${className || ''} ${
            selected ? 'sel' : ''
          }`.trim()}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            {icon && (
              <div className="icon-container">
                <FontAwesomeIcon
                  icon={!selected ? icon : faCheck}
                  className={selected ? 'bounceEnter' : 'bounceExit'}
                />
              </div>
            )}
            <div className="label">{label}</div>
          </div>
          {tooltipComponent && (
            <Tooltip arrow={true} title={tooltipComponent}>
              {/* div is required for MUI Tooltip reference */}
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
              </div>
            </Tooltip>
          )}
        </SelectorSecondary>
      );
    case 'secondary-no-icon':
      return (
        <SelectorSecondaryNoIcon
          className={`ti-sel-opt ${className || ''} ${
            selected ? 'sel' : ''
          }`.trim()}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            <div className="label">{label}</div>
          </div>
          {tooltipComponent && (
            <Tooltip arrow={true} title={tooltipComponent}>
              {/* div is required for MUI Tooltip reference */}
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
              </div>
            </Tooltip>
          )}
        </SelectorSecondaryNoIcon>
      );
    default:
    case 'radio':
      return (
        <SelectorSecondary
          margin={margin}
          padding={padding}
          maxWidth={maxWidth}
          animationScale={animationScale}
          className={`ti-sel-opt ${className || ''} ${
            selected ? 'sel' : ''
          }`.trim()}
          id={id}
          onClick={disabled ? undefined : onClick}
          selected={selected}
          color={color}
          disabled={disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
          title={titleText}
        >
          <div className="main-group">
            <div className="icon-container">
              {!selected && icon && <FontAwesomeIcon icon={icon} />}
              {!selected && !icon && <Radio variant="white" />}
              {selected && <Radio variant="white" checked={selected} />}
            </div>
            <div className="label">{label}</div>
          </div>
          {tooltipComponent && (
            <Tooltip arrow={true} title={tooltipComponent}>
              {/* div is required for MUI Tooltip reference */}
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon-text" />
              </div>
            </Tooltip>
          )}
        </SelectorSecondary>
      );
  }
};

export interface SelectorOptionsProps<T = string> {
  options: T[];
  className?: string;
  wrapperClassName?: string;
  tooltip?: (value: T) => NonNullable<React.ReactNode> | string;
  getLabel: (value: T, index: number) => TrueImpactSelectorProps['label'];
  getSelected: (value: T) => boolean;
  getDisabled?: (value: T) => boolean;
  getIcon?: (value: T) => IconProp | IconDefinition | undefined;
  onClick: (value: T, index: number) => unknown;
  variant?: TrueImpactSelectorProps['variant'];
  margin?: TrueImpactSelectorProps['margin'];
  padding?: TrueImpactSelectorProps['padding'];
  maxWidth?: TrueImpactSelectorProps['maxWidth'];
  disabled?: boolean;
}

export const MAX_WIDTH_FOR_SELECTOR = '600px';

const StyledDiv = styled.div`
  width: 480px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  gap: ${SPACERS.medium};
`;

export const SelectorOptions = <T,>({
  options,
  className,
  tooltip,
  getIcon,
  getLabel,
  getSelected,
  getDisabled,
  onClick,
  padding,
  wrapperClassName,
  variant = 'secondary-no-icon',
  margin = `${SPACERS.tiny} auto`,
  maxWidth = MAX_WIDTH_FOR_SELECTOR,
  disabled = false,
}: SelectorOptionsProps<T>) => {
  if (options.length === 2 && options[0] === 'Yes' && options[1] === 'No') {
    return (
      <StyledDiv className={wrapperClassName}>
        {options.map((o, i) => (
          <Selector
            key={i}
            icon={o === 'Yes' ? faCheck : faTimes}
            variant="primary-checkbox"
            label={getLabel(o, i)}
            selected={getSelected(o)}
            maxWidth={maxWidth}
            margin={margin}
            padding={padding}
            onClick={() => onClick(o, i)}
            className={className}
            disabled={disabled}
          />
        ))}
      </StyledDiv>
    );
  }
  return (
    <>
      {options.map((o, i) => {
        const props = {
          variant: variant,
          icon: getIcon ? getIcon(o) : undefined,
          label: getLabel(o, i),
          selected: getSelected(o),
          maxWidth: maxWidth,
          margin: margin,
          padding: padding,
          onClick: () => onClick(o, i),
          className: className,
          disabled: disabled || getDisabled?.(o),
        };
        return tooltip ? (
          <Tooltip key={i} arrow title={tooltip(o)}>
            <div style={{ width: '100%' }}>
              <Selector {...props} />
            </div>
          </Tooltip>
        ) : (
          <Selector key={i} {...props} />
        );
      })}
    </>
  );
};

export default Selector;
